/**
 * Mobile Menu - Tabbed navigation with slide-in submenus
 * Inspired by Camilla & Marc mobile navigation pattern
 */
class MobileMenu extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);

    // Listen to parent details open/close to auto-select the first tab when opened
    const parentDetails = this.closest('details');
    if (parentDetails) {
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
            if (parentDetails.hasAttribute('open')) {
              this.activateFirstTab();
            }
          }
        });
      });
      this.observer.observe(parentDetails, { attributes: true });
    }

    // Observe child mutations to react to morphing or late parsing of tabs/panels
    this.childObserver = new MutationObserver(() => {
      const activeTab = this.querySelector('.mobile-menu__tab--active');
      if (!activeTab) {
        this.activateFirstTab();
      }
    });
    this.childObserver.observe(this, { childList: true, subtree: true });

    // Defer initial activation to ensure child nodes are parsed
    setTimeout(() => {
      if (this.isConnected) {
        this.activateFirstTab();
      }
    }, 0);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.childObserver) {
      this.childObserver.disconnect();
    }
  }

  get tabs() {
    return this.querySelectorAll('.mobile-menu__tab');
  }

  get panels() {
    return this.querySelectorAll('.mobile-menu__panel');
  }

  get subpanels() {
    return this.querySelectorAll('.mobile-menu__subpanel');
  }

  #handleClick = (e) => {
    // 1. Tab switching
    const tab = e.target.closest('.mobile-menu__tab');
    if (tab) {
      e.preventDefault();
      const href = tab.getAttribute('data-href');
      const tabIndex = tab.getAttribute('data-tab');

      // If this tab has no panel (no children), navigate directly
      if (tabIndex === null || tabIndex === '') {
        if (href) window.location.href = href;
        return;
      }

      this.activateTab(tabIndex);
      return;
    }

    // 2. Submenu triggers
    const trigger = e.target.closest('[data-submenu]');
    if (trigger && this.contains(trigger)) {
      e.preventDefault();
      const submenuId = trigger.getAttribute('data-submenu');
      this.openSubpanel(submenuId);
      return;
    }

    // 3. Back buttons
    const backBtn = e.target.closest('.mobile-menu__back');
    if (backBtn && this.contains(backBtn)) {
      e.preventDefault();
      this.closeSubpanel();
      return;
    }

    // 4. Close button
    const closeBtn = e.target.closest('.mobile-menu__close');
    if (closeBtn && this.contains(closeBtn)) {
      e.preventDefault();
      this.closeDrawer();
      return;
    }
  };

  activateFirstTab() {
    if (this.childObserver) {
      this.childObserver.disconnect();
    }

    const tabs = this.tabs;
    if (tabs.length > 0) {
      const firstTab = tabs[0];
      const tabIndex = firstTab.getAttribute('data-tab');
      if (tabIndex !== null && tabIndex !== '') {
        this.activateTab(tabIndex);
      }
    }

    if (this.childObserver) {
      this.childObserver.observe(this, { childList: true, subtree: true });
    }
  }

  activateTab(tabIndex) {
    console.log('[MobileMenu] activateTab called with:', tabIndex);
    console.trace();
    // Close any open subpanels first
    this.closeSubpanel();

    // Update tab active states
    this.tabs.forEach((t) => {
      const tabId = t.getAttribute('data-tab');
      const isActive = tabId !== null && String(tabId).trim() === String(tabIndex).trim();
      t.classList.toggle('mobile-menu__tab--active', isActive);
    });

    // Update panel visibility
    this.panels.forEach((p) => {
      const panelId = p.getAttribute('data-panel');
      const isActive = panelId !== null && String(panelId).trim() === String(tabIndex).trim();
      p.classList.toggle('mobile-menu__panel--active', isActive);
    });
  }

  openSubpanel(submenuId) {
    const subpanel = this.querySelector(`[data-subpanel="${submenuId}"]`);
    if (subpanel) {
      subpanel.classList.add('mobile-menu__subpanel--active');
    }
  }

  closeSubpanel() {
    this.subpanels.forEach((sp) => {
      sp.classList.remove('mobile-menu__subpanel--active');
    });
  }

  closeDrawer() {
    const headerDrawer = this.closest('header-drawer');
    if (headerDrawer && typeof headerDrawer.close === 'function') {
      headerDrawer.close();
    }
  }
}

if (!customElements.get('mobile-menu')) {
  customElements.define('mobile-menu', MobileMenu);
}
